/**
 * OverdueFocusList — „Überfällig-Fokus" fürs Owner-Cockpit
 * (M-CFO-4, `architecture-cockpit-cfo.md` §8.4).
 *
 * Zeigt die überfälligen offenen Posten priorisiert nach ihrem Cash-Hebel
 * (`openAmountCents × Tage überfällig` — größter zuerst, NICHT chronologisch;
 * {@link rankOverdueFocus}). Jede Zeile ist ein Ein-Klick-Deep-Link zur
 * Offene-Posten-Seite, wo der geprüfte Mahn-Flow (`DunningModal`) lebt.
 *
 * Props-getrieben: konsumiert die auf CockpitPage-Ebene bereits geladenen
 * {@link CockpitOpenItemRow}[] (0 neue Firestore-Reads). Fehler-Zustand mit
 * isoliertem Retry (Muster `OpenItemsAgingChartTile`); leere Fokusliste ⇒
 * positiver Zero-State statt einer „0".
 *
 * @module components/cockpit/OverdueFocusList
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconError, IconSuccess, IconChevronRight } from '../../icons';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';
import { formatCurrency } from '../../utils/formatters';
import { centsToEuros } from '../../utils/moneyUtils';
import logger from '../../utils/logger';
import { rankOverdueFocus, type OverdueFocusRow } from '../../utils/cockpitReceivables';
import { loadOpenItemRows, type CockpitOpenItemRow } from '../../loaders/cockpitLoaders';

/** Today's date as ISO `YYYY-MM-DD` (local) — bewusst dupliziert (Muster `CockpitPage`). */
function todayISOdefault(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Anzeigename einer Fokuszeile: Kunde ⟶ Rechnungsnummer ⟶ gekürzte ID. */
function displayName(row: OverdueFocusRow): string {
  if (row.buyerName && row.buyerName.trim()) return row.buyerName;
  if (row.invoiceNumber && row.invoiceNumber.trim()) return row.invoiceNumber;
  return row.invoiceId.slice(0, 8);
}

export interface OverdueFocusListProps {
  /** Effektiver Scope-UID — für den isolierten Retry (`loadOpenItemRows`). */
  scopeUid: string;
  /** Gebubbelte offene-Posten-Zeilen; `null` ⇒ noch am Laden. */
  rows: CockpitOpenItemRow[] | null;
  /** Gebubbelter Ladefehler der offenen Posten. */
  rowsError: boolean;
  /** Maximale Zeilenzahl (Default 3). */
  topN?: number;
  /** Referenzdatum (Test-Injektion); Default = heute. */
  todayISO?: string;
}

type LocalState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: CockpitOpenItemRow[] };

/**
 * @param props - {@link OverdueFocusListProps}.
 */
const OverdueFocusList: React.FC<OverdueFocusListProps> = ({ scopeUid, rows, rowsError, topN = 3, todayISO }) => {
  const { t } = useTranslation('cockpit');
  const navigate = useNavigate();
  const [manual, setManual] = useState<LocalState | null>(null);
  const today = todayISO ?? todayISOdefault();

  const state: LocalState =
    manual ??
    (rowsError
      ? { status: 'error' }
      : rows !== null
        ? { status: 'ready', data: rows }
        : { status: 'loading' });

  const retry = async (): Promise<void> => {
    setManual({ status: 'loading' });
    try {
      const fresh = await loadOpenItemRows(scopeUid);
      setManual({ status: 'ready', data: fresh });
    } catch (err) {
      setManual({ status: 'error' });
      logger.error('CockpitPage: OverdueFocusList-Retry (offene Posten) fehlgeschlagen', err);
    }
  };

  return (
    <Card>
      <div className="flex items-center gap-2 mb-3 text-secondary">
        <IconError aria-hidden={true} />
        <h3 className="text-sm font-semibold">{t('cfo.focus.title')}</h3>
      </div>

      {state.status === 'loading' && (
        <div className="flex justify-center py-2">
          <Spinner size="sm" />
        </div>
      )}

      {state.status === 'error' && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-secondary">{t('empty.dash')}</span>
          <Button size="sm" variant="secondary" onClick={retry}>
            {t('retry')}
          </Button>
        </div>
      )}

      {state.status === 'ready' && (
        <FocusRows
          focus={rankOverdueFocus(state.data, today, topN)}
          navigate={navigate}
          t={t}
        />
      )}
    </Card>
  );
};

/** Reiner Präsentations-Teil: Fokus-Zeilen bzw. positiver Zero-State. */
const FocusRows: React.FC<{
  focus: OverdueFocusRow[];
  navigate: (to: string) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}> = ({ focus, navigate, t }) => {
  if (focus.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-sm text-secondary">
        <IconSuccess aria-hidden={true} className="text-[var(--tone-success-fg)] shrink-0" />
        {t('cfo.focus.empty')}
      </p>
    );
  }

  const go = () => navigate('/dashboard/offene-posten');

  return (
    <ul className="divide-y divide-white/5">
      {focus.map((row) => (
        <li key={row.invoiceId}>
          <div
            role="button"
            tabIndex={0}
            onClick={go}
            onKeyDown={(e) => e.key === 'Enter' && go()}
            className="flex items-center gap-3 py-2 cursor-pointer"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-primary truncate">{displayName(row)}</p>
              <p className="text-xs text-secondary">
                {t('cfo.focus.daysOverdue', { count: row.daysOverdue })}
              </p>
            </div>
            <span className="text-sm font-semibold text-primary shrink-0">
              {formatCurrency(centsToEuros(row.openAmountCents))}
            </span>
            <IconChevronRight aria-hidden={true} size={12} className="text-muted shrink-0" />
          </div>
        </li>
      ))}
    </ul>
  );
};

export default OverdueFocusList;
